/**
 * SuperAdminService - ラクハイ管理者専用サービス
 * 陸送会社管理者アカウント作成・管理機能を提供
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { User, Role } from '../types';
import { getSupabaseClient } from '../utils/supabase';
import { DataServiceError, ErrorCodes } from './dataService';
import logger from '../utils/logger';

export interface CompanyAdminCreateData {
  email: string;
  password: string;
  name: string;
  companyId: string;
  phone?: string;
}

export interface CompanyAdminUpdateData {
  name?: string;
  email?: string;
  phone?: string;
  status?: 'active' | 'inactive' | 'suspended';
}

export interface SystemStats {
  totalCompanies: number;
  activeCompanies: number;
  totalUsers: number;
  totalAdmins: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
  lastBackup?: string;
}

export interface AuditLog {
  id: string;
  userId: string;
  userEmail: string;
  action: string;
  targetType: string;
  targetId: string;
  details: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: Role;
  company_id?: string;
  company_name?: string;
  land_transporter_id?: string;
  created_at: string;
}

export class SuperAdminService {
  private supabase: SupabaseClient | null;

  constructor() {
    this.supabase = getSupabaseClient();
    logger.info('✅ SuperAdminService: シングルトンクライアント取得完了');
  }

  /**
   * Supabaseクライアントの可用性チェック
   */
  private ensureSupabaseClient(): SupabaseClient {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
    return this.supabase;
  }

  /**
   * 権限チェック: super_admin権限があるかを確認
   */
  private async checkSuperAdminPermission(): Promise<void> {
    logger.info('🔍 SuperAdminService: checkSuperAdminPermission開始');

    // 1. localStorage認証状態確認（優先）
    const AUTH_STORAGE_KEY = 'fleetpilot_mock_current_user';
    const storedUserData = localStorage.getItem(AUTH_STORAGE_KEY);

    if (storedUserData) {
      try {
        const user = JSON.parse(storedUserData);
        if (user && user.role === 'super_admin') {
          logger.info('✅ SuperAdminService: localStorage super_admin権限確認完了');
          return;
        }
      } catch (error) {
        logger.warn('⚠️ SuperAdminService: localStorage認証データ解析失敗:', error);
      }
    }

    // 2. Supabase認証確認（フォールバック）
    const supabase = this.ensureSupabaseClient();
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    logger.info('🔍 SuperAdminService: getUser結果:', { hasUser: !!user, error, userId: user?.id });

    if (error || !user) {
      throw new DataServiceError('認証が必要です', ErrorCodes.AUTHENTICATION_FAILED);
    }

    // ユーザープロファイル確認
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    logger.info('🔍 SuperAdminService: プロファイル確認:', {
      profile,
      profileError,
      userId: user.id,
    });

    if (profileError || !profile) {
      throw new DataServiceError(
        'ユーザープロファイルが見つかりません',
        ErrorCodes.PERMISSION_DENIED
      );
    }

    if (profile.role !== 'super_admin') {
      throw new DataServiceError(
        'この操作にはラクハイ管理者権限が必要です',
        ErrorCodes.PERMISSION_DENIED
      );
    }

    logger.info('✅ SuperAdminService: Supabase super_admin権限確認完了');
  }

  /**
   * 陸送会社管理者アカウント作成（ラクハイ管理者のみ）
   */
  async createCompanyAdmin(adminData: CompanyAdminCreateData): Promise<User> {
    await this.checkSuperAdminPermission();

    try {
      // 会社存在確認
      const supabase = this.ensureSupabaseClient();
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('id, name, subscription_status')
        .eq('id', adminData.companyId)
        .single();

      if (companyError || !company) {
        throw new DataServiceError('指定された会社が見つかりません', ErrorCodes.RESOURCE_NOT_FOUND);
      }

      if (company.subscription_status !== 'active' && company.subscription_status !== 'trial') {
        throw new DataServiceError(
          'この会社は現在アクティブではありません',
          ErrorCodes.VALIDATION_ERROR
        );
      }

      // 既存の管理者チェック
      const { data: existingAdmin } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('company_id', adminData.companyId)
        .eq('role', 'admin')
        .single();

      if (existingAdmin) {
        throw new DataServiceError(
          'この会社には既に管理者が存在します。まず既存の管理者を削除してください。',
          ErrorCodes.VALIDATION_ERROR
        );
      }

      // Supabaseユーザー作成
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: adminData.email,
        password: adminData.password,
        options: {
          data: {
            name: adminData.name,
            role: 'admin',
            company_id: adminData.companyId,
          },
        },
      });

      if (authError) {
        throw new DataServiceError(
          `管理者アカウント作成に失敗しました: ${authError.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      if (!authData.user) {
        throw new DataServiceError(
          '管理者アカウント作成に失敗しました',
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      // ユーザープロファイル作成
      await this.createAdminProfile(authData.user.id, {
        name: adminData.name,
        role: 'admin' as Role,
        companyId: adminData.companyId,
      });

      // 監査ログ記録
      await this.logAdminAction('create_company_admin', 'user', authData.user.id, {
        email: adminData.email,
        companyId: adminData.companyId,
        companyName: company.name,
      });

      logger.info(`Company admin created: ${adminData.email} for company ${company.name}`);

      return {
        id: crypto.randomUUID(),
        name: adminData.name,
        role: 'admin' as Role,
        company: company.name,
        companyId: adminData.companyId,
      };
    } catch (error) {
      logger.error('SuperAdminService: Create company admin failed:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送会社管理者作成に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 管理者プロファイル作成
   */
  private async createAdminProfile(
    userId: string,
    userData: {
      name: string;
      role: Role;
      companyId: string;
    }
  ): Promise<void> {
    const supabase = this.ensureSupabaseClient();
    const { error } = await supabase.rpc('create_user_profile_with_metadata', {
      user_id: userId,
      user_name: userData.name,
      user_role: userData.role,
      company_id: userData.companyId,
      land_transporter_id: null,
    });

    if (error) {
      throw new DataServiceError(
        `管理者プロファイル作成に失敗しました: ${error.message}`,
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }
  }

  /**
   * システム統計情報取得（ラクハイ管理者向けダッシュボード）
   */
  async getSystemStats(): Promise<SystemStats> {
    logger.info('🔍 SuperAdminService: getSystemStats開始');
    await this.checkSuperAdminPermission();

    try {
      logger.info('🔍 SuperAdminService: 統計データ取得中...');
      // 並行実行で各種統計を取得
      const supabase = this.ensureSupabaseClient();
      const [companiesResult, activeCompaniesResult, usersResult, adminsResult] = await Promise.all(
        [
          supabase.from('companies').select('id', { count: 'exact', head: true }),
          supabase
            .from('companies')
            .select('id', { count: 'exact', head: true })
            .eq('subscription_status', 'active'),
          supabase.from('user_profiles').select('id', { count: 'exact', head: true }),
          supabase
            .from('user_profiles')
            .select('id', { count: 'exact', head: true })
            .in('role', ['super_admin', 'admin']),
        ]
      );

      logger.info('📊 SuperAdminService: クエリ結果:', {
        companiesResult: companiesResult.count,
        activeCompaniesResult: activeCompaniesResult.count,
        usersResult: usersResult.count,
        adminsResult: adminsResult.count,
      });

      // システムヘルス判定（簡易実装）
      const totalCompanies = companiesResult.count || 0;
      const activeCompanies = activeCompaniesResult.count || 0;
      const activeRatio = totalCompanies > 0 ? activeCompanies / totalCompanies : 1;

      let systemHealth: 'healthy' | 'warning' | 'critical' = 'healthy';
      if (activeRatio < 0.5) {
        systemHealth = 'critical';
      } else if (activeRatio < 0.8) {
        systemHealth = 'warning';
      }

      const stats = {
        totalCompanies,
        activeCompanies,
        totalUsers: usersResult.count || 0,
        totalAdmins: adminsResult.count || 0,
        systemHealth,
        lastBackup: undefined, // TODO: バックアップシステム実装時に追加
      };

      logger.info('✅ SuperAdminService: システム統計取得完了:', stats);
      return stats;
    } catch (error) {
      logger.error('❌ SuperAdminService: Get system stats failed:', error);
      throw new DataServiceError(
        'システム統計情報取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 管理者アクション監査ログ記録
   */
  private async logAdminAction(
    action: string,
    targetType: string,
    targetId: string,
    details: Record<string, unknown>
  ): Promise<void> {
    if (!this.supabase) return;

    try {
      const supabase = this.ensureSupabaseClient();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // 簡易実装：audit_logsテーブルが存在する場合の処理
      // 実際の実装では適切な監査ログテーブルを作成する必要があります
      logger.info('Audit Log:', {
        userId: user.id,
        userEmail: user.email,
        action,
        targetType,
        targetId,
        details,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      logger.error('Failed to log admin action:', error);
      // 監査ログの失敗は業務処理に影響させない
    }
  }

  /**
   * 陸送会社管理者パスワードリセット
   */
  async resetCompanyAdminPassword(adminEmail: string): Promise<void> {
    await this.checkSuperAdminPermission();

    try {
      const supabase = this.ensureSupabaseClient();
      const { error } = await supabase.auth.resetPasswordForEmail(adminEmail);

      if (error) {
        throw new DataServiceError(
          `パスワードリセットに失敗しました: ${error.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      // 監査ログ記録
      await this.logAdminAction('reset_admin_password', 'user', adminEmail, {
        email: adminEmail,
      });

      logger.info(`Password reset initiated for admin: ${adminEmail}`);
    } catch (error) {
      logger.error('SuperAdminService: Reset admin password failed:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'パスワードリセットに失敗しました',
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 陸送会社管理者アカウント無効化
   */
  async deactivateCompanyAdmin(adminId: string): Promise<void> {
    await this.checkSuperAdminPermission();

    try {
      // 管理者情報取得
      const supabase = this.ensureSupabaseClient();
      const { data: adminProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('name, company_id, role')
        .eq('id', adminId)
        .single();

      if (profileError || !adminProfile) {
        throw new DataServiceError(
          '指定された管理者が見つかりません',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      if (adminProfile.role !== 'admin') {
        throw new DataServiceError(
          '指定されたユーザーは管理者ではありません',
          ErrorCodes.VALIDATION_ERROR
        );
      }

      // TODO: Supabase Authのユーザー無効化API実装
      // 現在はプロファイルにstatusフィールドを追加して管理する方法も検討

      // 監査ログ記録
      await this.logAdminAction('deactivate_admin', 'user', adminId, {
        adminName: adminProfile.name,
        companyId: adminProfile.company_id,
      });

      logger.info(`Company admin deactivated: ${adminId}`);
    } catch (error) {
      logger.error('SuperAdminService: Deactivate admin failed:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '管理者アカウント無効化に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 全システムユーザー一覧取得（検索・フィルタ機能付き）
   */
  async getUsersWithFilters(filters?: {
    role?: string;
    companyId?: string;
    searchQuery?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ users: User[]; totalCount: number }> {
    await this.checkSuperAdminPermission();

    try {
      const supabase = this.ensureSupabaseClient();
      let query = supabase.from('user_profiles').select('*, companies(name)', { count: 'exact' });

      // フィルタリング
      if (filters?.role) {
        query = query.eq('role', filters.role);
      }

      if (filters?.companyId) {
        query = query.eq('company_id', filters.companyId);
      }

      if (filters?.searchQuery) {
        query = query.ilike('name', `%${filters.searchQuery}%`);
      }

      // ソート・ページネーション
      query = query.order('created_at', { ascending: false });

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
      }

      const { data, error, count } = await query;

      if (error) {
        throw new DataServiceError(
          `ユーザー一覧取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      const users: User[] = (data || []).map(profile => ({
        id: profile.auth_user_id || crypto.randomUUID(),
        name: profile.name,
        role: profile.role as Role,
        company: profile.companies?.name || '未設定',
        companyId: profile.company_id,
        landTransporterId: profile.land_transporter_id,
      }));

      return {
        users,
        totalCount: count || 0,
      };
    } catch (error) {
      logger.error('SuperAdminService: Get all users failed:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ユーザー一覧取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 全ユーザープロファイル一覧取得（UserProfile形式）
   */
  async getAllUsers(): Promise<UserProfile[]> {
    await this.checkSuperAdminPermission();

    try {
      // まずuser_profilesから基本データを取得
      const supabase = this.ensureSupabaseClient();
      const { data: profilesData, error } = await supabase
        .from('user_profiles')
        .select(
          `
          id,
          name,
          role,
          company_id,
          land_transporter_id,
          created_at
        `
        )
        .order('created_at', { ascending: false });

      if (error) {
        throw new DataServiceError(
          `ユーザー一覧取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      // 会社情報を別途取得
      const { data: companiesData } = await supabase.from('companies').select('id, name');

      // 会社IDと名前のマッピングを作成
      const companyMap = new Map();
      if (companiesData) {
        companiesData.forEach(company => {
          companyMap.set(company.id, company.name);
        });
      }

      // company_usersテーブルからメールアドレスを取得
      const { data: companyUsersData } = await supabase
        .from('company_users')
        .select('auth_user_id, email');

      // ユーザーIDとメールアドレスのマッピングを作成
      const emailMap = new Map();
      if (companyUsersData) {
        companyUsersData.forEach(cu => {
          emailMap.set(cu.auth_user_id, cu.email);
        });
      }

      // プロファイルデータを処理
      const profiles: UserProfile[] = (profilesData || []).map(profile => {
        return {
          id: profile.id,
          name: profile.name,
          email: emailMap.get(profile.id) || 'N/A',
          role: profile.role as Role,
          company_id: profile.company_id,
          company_name: profile.company_id ? companyMap.get(profile.company_id) : undefined,
          land_transporter_id: profile.land_transporter_id,
          created_at: profile.created_at,
        };
      });

      logger.info(`✅ SuperAdminService: 全ユーザー取得完了 (${profiles.length}人)`);
      return profiles;
    } catch (error) {
      logger.error('SuperAdminService: Get all users failed:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ユーザー一覧取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 陸送会社管理者情報取得（ID指定）
   */
  async getCompanyAdminById(adminId: string): Promise<UserProfile> {
    await this.checkSuperAdminPermission();

    try {
      const supabase = this.ensureSupabaseClient();

      // user_profilesから基本情報を取得
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, name, role, company_id, created_at')
        .eq('id', adminId)
        .eq('role', 'admin')
        .single();

      if (profileError || !profile) {
        throw new DataServiceError(
          '指定された管理者が見つかりません',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      // company_usersからメールアドレスと電話番号を取得
      const { data: companyUser } = await supabase
        .from('company_users')
        .select('email, phone')
        .eq('auth_user_id', adminId)
        .single();

      // 会社情報を取得
      let companyName = null;
      if (profile.company_id) {
        const { data: company } = await supabase
          .from('companies')
          .select('name')
          .eq('id', profile.company_id)
          .single();
        companyName = company?.name;
      }

      return {
        id: profile.id,
        name: profile.name,
        email: companyUser?.email || 'N/A',
        phone: companyUser?.phone,
        role: profile.role as Role,
        company_id: profile.company_id,
        company_name: companyName,
        created_at: profile.created_at,
      };
    } catch (error) {
      logger.error('SuperAdminService: Get company admin failed:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '管理者情報取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 陸送会社管理者情報更新
   */
  async updateCompanyAdmin(adminId: string, updateData: CompanyAdminUpdateData): Promise<User> {
    await this.checkSuperAdminPermission();

    try {
      const supabase = this.ensureSupabaseClient();

      // 管理者存在確認
      const { data: existingProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, name, role, company_id')
        .eq('id', adminId)
        .eq('role', 'admin')
        .single();

      if (profileError || !existingProfile) {
        throw new DataServiceError(
          '指定された管理者が見つかりません',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      // user_profilesの更新（名前のみ）
      if (updateData.name) {
        const { error: updateProfileError } = await supabase
          .from('user_profiles')
          .update({ name: updateData.name })
          .eq('id', adminId);

        if (updateProfileError) {
          throw new DataServiceError(
            `プロファイル更新に失敗しました: ${updateProfileError.message}`,
            ErrorCodes.DATA_UPDATE_FAILED
          );
        }
      }

      // company_usersの更新（メール、電話番号、ステータス）
      const companyUserUpdates: Record<string, any> = {};
      if (updateData.email) companyUserUpdates.email = updateData.email;
      if (updateData.phone !== undefined) companyUserUpdates.phone = updateData.phone;
      if (updateData.status) companyUserUpdates.status = updateData.status;

      if (Object.keys(companyUserUpdates).length > 0) {
        const { error: updateCompanyUserError } = await supabase
          .from('company_users')
          .update(companyUserUpdates)
          .eq('auth_user_id', adminId);

        if (updateCompanyUserError) {
          throw new DataServiceError(
            `ユーザー情報更新に失敗しました: ${updateCompanyUserError.message}`,
            ErrorCodes.DATA_UPDATE_FAILED
          );
        }
      }

      // 会社情報取得
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', existingProfile.company_id)
        .single();

      // 監査ログ記録
      await this.logAdminAction('update_company_admin', 'user', adminId, {
        updates: updateData,
        companyId: existingProfile.company_id,
        companyName: company?.name,
      });

      logger.info(`Company admin updated: ${adminId}`);

      return {
        id: adminId,
        name: updateData.name || existingProfile.name,
        role: 'admin' as Role,
        company: company?.name || '不明',
        companyId: existingProfile.company_id,
      };
    } catch (error) {
      logger.error('SuperAdminService: Update company admin failed:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '管理者情報更新に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * ユーザー削除（ラクハイ管理者のみ）
   * auth.users、user_profiles、company_usersから完全削除
   */
  async deleteUser(userId: string): Promise<void> {
    await this.checkSuperAdminPermission();

    try {
      const supabase = this.ensureSupabaseClient();

      // ユーザー情報取得（監査ログ用）
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('name, role, company_id')
        .eq('id', userId)
        .single();

      if (profileError) {
        throw new DataServiceError(
          '指定されたユーザーが見つかりません',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      // super_adminの削除を防止
      if (userProfile.role === 'super_admin') {
        throw new DataServiceError(
          'ラクハイ管理者アカウントは削除できません',
          ErrorCodes.VALIDATION_ERROR
        );
      }

      // 削除順序: company_users → user_profiles → auth.users
      // 外部キー制約を考慮して、依存テーブルから順に削除

      // 1. company_usersから削除（外部キー: auth_user_id）
      const { error: companyUserError } = await supabase
        .from('company_users')
        .delete()
        .eq('auth_user_id', userId);

      if (companyUserError) {
        logger.warn(`company_users削除警告: ${companyUserError.message}`);
        // company_usersのレコードが存在しない場合もあるため、警告のみ
      }

      // 2. user_profilesから削除（主テーブル）
      const { error: userProfileError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', userId);

      if (userProfileError) {
        throw new DataServiceError(
          `ユーザープロファイル削除に失敗しました: ${userProfileError.message}`,
          ErrorCodes.DATA_DELETION_FAILED
        );
      }

      // 3. auth.usersから削除（Supabase Admin API）
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);

      if (authError) {
        logger.warn(`auth.users削除警告: ${authError.message}`);
      }

      // 監査ログ記録
      await this.logAdminAction('delete_user', 'user', userId, {
        userName: userProfile.name,
        userRole: userProfile.role,
        companyId: userProfile.company_id,
      });

      logger.info(`✅ ユーザー削除完了: ${userId} (${userProfile.name})`);
    } catch (error) {
      logger.error('SuperAdminService: Delete user failed:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ユーザー削除に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }
}
